import React, { useContext, useState } from "react";
import { CircularProgress, Grid, makeStyles } from "@material-ui/core";
import { Dropdown, IDropdownOption } from "@fluentui/react";
import {
  ClassScheduleApi,
  ClassScheduleDto,
  DayOfWeek,
} from "../../api-clients/api";
import { getApiUrl } from "../../api-clients";
import { AppContext } from "../../contexts/AppContext";
import { SchoolContext } from "../../contexts/SchoolContext";
import FormButtons from "../Shared/FormButtons";
import FluentUiTimePicker from "../Shared/FluentUiTimePicker";
import SubjectsDropdown from "../Shared/SubjectsDropdown";
import TeachersDropdown from "../Shared/TeachersDropdown";
import {
  dateToTimeSpan,
  daysOfWeekOrdered,
  dayOfWeekToString,
  timeSpanToDate,
} from "../../helper";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
  wrapper: {
    margin: theme.spacing(1),
    position: "relative",
  },
}));

interface IProps {
  classSchedule: ClassScheduleDto;
  classroomId: string;
  scheduleChanged?: () => void;
}
export default function CRUDClassSchedule({
  classSchedule,
  classroomId,
  scheduleChanged,
}: IProps) {
  const classes = useStyles();
  const { t } = useTranslation();
  const { openSnackbar } = useContext(AppContext);
  const { accademicYear } = useContext(SchoolContext);
  const [saving, setSaving] = useState<boolean>(false);
  const [classScheduleApi] = useState<ClassScheduleApi>(
    new ClassScheduleApi(getApiUrl())
  );
  const [form, setForm] = useState<ClassScheduleDto>(classSchedule);

  const canSave: boolean =
    form.dayOfWeek != null &&
    form.startTime != null &&
    form.endTime != null &&
    (form.subjectId?.length ?? 0) > 0 &&
    (form.teacherId?.length ?? 0) > 0;

  const save = () => {
    if (!canSave || accademicYear?.accademicYearId == null) return;
    setSaving(true);
    const payload = {
      ...form,
      classroomId: classroomId,
      accademicYearId: accademicYear.accademicYearId,
    } as ClassScheduleDto;
    const action = form.classScheduleId
      ? classScheduleApi.update(payload)
      : classScheduleApi.create(payload);
    action
      .then(() => {
        setSaving(false);
        openSnackbar(t("classSchedule.saveSuccess"), "success");
        if (scheduleChanged) scheduleChanged();
      })
      .catch(() => {
        setSaving(false);
        openSnackbar(t("common.errorOccurred"), "error");
      });
  };

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Dropdown
          required
          label={t("classSchedule.day")}
          selectedKey={form.dayOfWeek}
          options={daysOfWeekOrdered.map((d) => ({
            key: d,
            text: dayOfWeekToString(d),
          }))}
          onChange={(
            event: React.FormEvent<HTMLDivElement>,
            option?: IDropdownOption
          ) => {
            if (option)
              setForm({
                ...form,
                dayOfWeek: option.key as DayOfWeek,
              } as ClassScheduleDto);
          }}
        />
      </Grid>
      <Grid item xs={6}>
        <FluentUiTimePicker
          required
          label={t("classSchedule.startTime")}
          value={timeSpanToDate(form.startTime)}
          onSelectTime={(date: Date) =>
            setForm({
              ...form,
              startTime: dateToTimeSpan(date),
            } as ClassScheduleDto)
          }
        />
      </Grid>
      <Grid item xs={6}>
        <FluentUiTimePicker
          required
          label={t("classSchedule.endTime")}
          value={timeSpanToDate(form.endTime)}
          onSelectTime={(date: Date) =>
            setForm({
              ...form,
              endTime: dateToTimeSpan(date),
            } as ClassScheduleDto)
          }
        />
      </Grid>
      <Grid item xs={12}>
        <SubjectsDropdown
          required
          label={t("classSchedule.subject")}
          placeholder={t("classSchedule.selectSubject")}
          selectedKey={form.subjectId}
          onChange={(
            event: React.FormEvent<HTMLDivElement>,
            option?: IDropdownOption
          ) => {
            if (option)
              setForm({
                ...form,
                subjectId: option.key as string,
              } as ClassScheduleDto);
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <TeachersDropdown
          required
          label={t("classSchedule.teacher")}
          placeholder={t("classSchedule.selectTeacher")}
          selectedKey={form.teacherId}
          onChange={(
            event: React.FormEvent<HTMLDivElement>,
            option?: IDropdownOption
          ) => {
            if (option)
              setForm({
                ...form,
                teacherId: option.key as string,
              } as ClassScheduleDto);
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <div className={classes.wrapper}>
          <FormButtons
            onConfirmClick={save}
            disabled={saving || !canSave}
            cancelButtonVisible={false}
            confirmIcon={form.classScheduleId != null ? "Save" : "Add"}
            confirmText={
              form.classScheduleId != null
                ? t("common.save")
                : t("common.create")
            }
          />
          {saving && (
            <CircularProgress
              size={24}
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                marginTop: -12,
                marginLeft: -12,
              }}
            />
          )}
        </div>
      </Grid>
    </Grid>
  );
}
